import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchApplicationDetails } from '../../../utils/apiUtils';
import styled from 'styled-components';
import { saveFormData } from '../../../utils/apiUtils';
import FilePreviewModal from '../../common/FilePreviewModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FormPageNavigator from '../../common/FormPageNavigator';
import SessionTimer from '../../common/SessionTimer';

// Helper function to generate a unique application ID
const generateApplicationId = (): string => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
  const year = String(now.getFullYear());
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');
  const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
  const randomChar = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'[Math.floor(Math.random() * 32)];
  return `CNG-${day}${month}${year}${hours}${minutes}${seconds}-${milliseconds}${randomChar}`;
};

// A4 Dimensions (approximate for screen, maintaining aspect ratio)
// 210mm / 297mm = 0.707
const A4_WIDTH_PX = 794; // Approx 210mm at 96 DPI
const A4_HEIGHT_PX = 1123; // Approx 297mm at 96 DPI

// Progress bar at top of page
const ProgressBarContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  z-index: 1000;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const ProgressBarFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #6cb288;
  transition: width 0.5s ease-in-out;
`;

const ProgressLabel = styled.div<{ visible?: boolean }>`
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: #f8fcfa;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  transition: opacity 0.3s ease;
  opacity: ${props => props.visible ? 1 : 0};
`;

// Zoom controls
const ZoomControlsContainer = styled.div`
  position: fixed;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
`;

const ZoomButton = styled.button`
  width: 30px;
  height: 30px;
  background-color: white;
  color: #6cb288;
  border: 1px solid #a8d0b9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 18px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  padding: 0;
  
  &:hover {
    background-color: #f8fcfa;
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const OfflineIndicator = styled.div`
  background-color: #dc3545; /* Red for offline */
  color: white;
  text-align: center;
  padding: 10px;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 2000; /* Ensure it's above other elements */
  font-size: 0.9em;
`;

const PageWrapper = styled.div<{ scale: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  min-height: 100vh;
  background-color: #f5f5f5;
  transform: scale(${props => props.scale});
  transform-origin: center top;
  transition: transform 0.3s ease;
`;

const A4PageContainer = styled.div`
  width: ${A4_WIDTH_PX}px;
  min-height: ${A4_HEIGHT_PX}px; /* Use min-height to allow content to expand if needed */
  background-color: white;
  padding: 50px; /* Increased padding for more breathing room */
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07); /* Softer, more modern shadow */
  box-sizing: border-box;
  position: relative; /* For page number positioning */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
  color: #212529; /* Darker, more standard text color */
  font-size: 13px; /* Base font size for content */
  line-height: 1.6;
`;

const FloatingBackButton = styled.button`
  position: fixed;
  bottom: 30px;
  left: 30px;
  background: linear-gradient(145deg, #4a6cf7, #5e54e2); /* Modern gradient */
  color: white;
  border: none;
  border-radius: 50%; /* Circular button */
  width: 60px; /* Size of the button */
  height: 60px;
  font-size: 22px; /* Slightly smaller icon size for a cleaner look */
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 6px 16px rgba(74, 108, 247, 0.3), 0 3px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000; /* Ensure it's on top */
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  overflow: hidden;
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 10px 20px rgba(74, 108, 247, 0.4), 0 6px 6px rgba(0, 0, 0, 0.1);
    
    &::before {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0) scale(0.98);
    box-shadow: 0 3px 8px rgba(74, 108, 247, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  &::after {
    content: 'Back';
    position: absolute;
    font-size: 12px;
    bottom: 7px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const FormContentContainer = styled.div`
  /* Empty form content container for template */
`;

const PageNumber = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  font-size: 0.8em;
  color: #777;
`;

const NextPageButton = styled.button`
  position: absolute;
  bottom: 15px;
  right: 60px;
  background-color: #6cb288;
  color: white;
  padding: 8px 25px;
  border: none;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #4d9e70;
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const SaveIndicator = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 50%;
  left: 20px;
  transform: translateY(-50%);
  background-color: #f8fcfa;
  color: #6cb288;
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid #a8d0b9;
  font-size: 0.8em;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  z-index: 10000;
`;

const SaveDraftButton = styled.button`
  background-color: #ffc107; /* Amber color for draft */
  color: #212529; /* Dark text for contrast */
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  font-size: 1em;
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 15px; /* Space from submit button or other elements */
  margin-right: 10px; /* Space from submit button if on same line */
  display: inline-block; /* Allow side-by-side with submit if desired */
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #e0a800; /* Darker amber on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #c69500;
    transform: translateY(1px);
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
  }
`;

const PayNowButton = styled.button`
  background-color: #007bff;
  color: white;
  padding: 10px 25px;
  border: none;
  border-radius: 6px; /* Slightly larger radius */
  font-size: 1em; /* Relative to base */
  font-weight: 500;
  letter-spacing: 0.5px;
  cursor: pointer;
  margin-top: 30px; /* More space above */
  display: block; 
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.15s ease-in-out, transform 0.1s ease-in-out;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);

  &:hover {
    background-color: #0069d9; /* Darker shade on hover */
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  }

  &:active {
    background-color: #0056b3;
    transform: translateY(1px); /* Slight press effect */
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
  }
`;

const ActionButtonsContainer = styled.div`
  margin-top: 30px;
  width: 100%;
`;

// Styled components for the certificate
const CertificateTitle = styled.h2`
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 10px;
  text-transform: uppercase;
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: 11px;
  margin-bottom: 30px;
  font-style: italic;
  text-transform: uppercase;
`;

const ToWhomSection = styled.p`
  font-weight: 500;
  margin-bottom: 20px;
  text-decoration: underline;
`;

const CertificateText = styled.p`
  text-align: justify;
  line-height: 1.8;
  margin-bottom: 15px;
`;

const InputField = styled.input`
  border: none;
  border-bottom: 1px solid #333;
  padding: 2px 5px;
  margin: 0 5px;
  background-color: transparent;
  font-size: 13px;
  font-family: inherit;
  min-width: 50px;
  
  &:focus {
    outline: none;
    border-bottom: 1px solid #6cb288;
  }
`;

const FacilityList = styled.div`
  margin: 20px 0;
  padding-left: 20px;
`;

const FacilityItem = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
`;

const FacilityLabel = styled.span`
  margin-right: 10px;
  font-weight: 500;
`;

const FacilityInput = styled(InputField)`
  flex: 1;
  min-width: 300px;
`;

const ClosingSection = styled.div`
  margin-top: 80px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const SignatureBox = styled.div`
  width: 250px;
  text-align: center;
  margin-bottom: 20px;
`;

const SignatureLine = styled.div`
  border-bottom: 1px solid #333;
  margin-bottom: 10px;
  height: 60px;
`;

const SignatureLabel = styled.p`
  font-size: 12px;
  margin: 5px 0;
`;

const SealBox = styled.div`
  width: 150px;
  height: 150px;
  border: 2px dashed #999;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 20px 0;
  color: #999;
  font-size: 11px;
`;

const DateField = styled.div`
  margin-top: 20px;
  display: flex;
  align-items: center;
`;

interface FormData {
  applicationNoOfficeUse: string;
  // Certificate fields
  customerName: string;
  yearsAsCustomer: string;
  facilityA: string;
  facilityB: string;
  facilityC: string;
  bankName: string;
  loanAmountNumber: string;
  loanAmountWords: string;
  signatoryName: string;
  signatoryDesignation: string;
  certificateDate: string;
  errors?: {
    customerName?: string;
    yearsAsCustomer?: string;
    facilityA?: string;
    facilityB?: string;
    facilityC?: string;
    bankName?: string;
    loanAmountNumber?: string;
    loanAmountWords?: string;
    signatoryName?: string;
    signatoryDesignation?: string;
    certificateDate?: string;
  };
}

interface ApplicationFormPage11Props {
  formData?: any;
  isViewMode?: boolean;
}

const initialState: FormData = {
  applicationNoOfficeUse: '',
  customerName: '',
  yearsAsCustomer: '',
  facilityA: '',
  facilityB: '',
  facilityC: '',
  bankName: '',
  loanAmountNumber: '',
  loanAmountWords: '',
  signatoryName: '',
  signatoryDesignation: '',
  certificateDate: '',
  errors: {}
};

const ApplicationFormPage11: React.FC<ApplicationFormPage11Props> = ({ isViewMode = false, formData: propFormData }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [applicationId, setApplicationId] = useState<string>('');
  const isNewlyGeneratedIdRef = useRef<boolean>(false);
  
  // Progress bar state
  const [formProgress, setFormProgress] = useState<number>(0);
  
  // Zoom state
  const [zoomScale, setZoomScale] = useState<number>(1.0);
  
  // Check if Fill Dummy Data button should be shown (controlled from Admin Controls, default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  
  // Fetch showDummyDataButton setting from backend
  useEffect(() => {
    const fetchDummyDataSetting = async () => {
      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const API_BASE = API_URL === '/api' ? '' : API_URL;
        const response = await fetch(`${API_BASE}/api/public/settings`);
        if (response.ok) {
          const data = await response.json();
          const settings = data.settings || data;
          if (settings && typeof settings.showDummyDataButton === 'boolean') {
            setShowDummyDataButton(settings.showDummyDataButton);
          }
        }
      } catch (error) {
        console.error('Error fetching dummy data button setting:', error);
      }
    };
    fetchDummyDataSetting();
  }, []);
  
  // Form data state
  const [formData, setFormData] = useState<FormData>(propFormData || { ...initialState });

  // State for save indicator
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveMessage, setSaveMessage] = useState<string>('Saved');
  const [showSaveIndicator, setShowSaveIndicator] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  
  // State for PDF generation modal
  const [isPdfGenerating, setIsPdfGenerating] = useState<boolean>(false);
  const [compressionProgress, setCompressionProgress] = useState<number>(0);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalFileUrl, setModalFileUrl] = useState<string | null>(null);
  const [modalFileName, setModalFileName] = useState<string | null>(null);
  const [modalFileType, setModalFileType] = useState<string | null>(null);
  
  // Save timeout reference for debouncing
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Effect to initialize or generate applicationId
  useEffect(() => {
    // If formData is passed as a prop, use its ID and skip the rest of the effect.
    if (propFormData?.applicationNoOfficeUse) {
      setApplicationId(propFormData.applicationNoOfficeUse);
      return;
    }
    
    let currentApplicationId = location.state?.applicationId || sessionStorage.getItem('currentApplicationId');
    let source = '';

    if (currentApplicationId) {
      source = location.state?.applicationId ? 'location.state' : 'sessionStorage';
      console.log(`[ApplicationFormPage11 Effect InitID] Found existing Application ID: ${currentApplicationId} from ${source}`);
      setApplicationId(currentApplicationId);
      if (formData.applicationNoOfficeUse !== currentApplicationId) {
        setFormData(prev => ({ ...prev, applicationNoOfficeUse: currentApplicationId }));
      }
    } else {
      const newId = generateApplicationId();
      console.log(`[ApplicationFormPage11 Effect InitID] No existing ID found. Generated new Application ID: ${newId}`);
      setApplicationId(newId);
      sessionStorage.setItem('currentApplicationId', newId);
      isNewlyGeneratedIdRef.current = true;
      setFormData(prev => ({ ...prev, applicationNoOfficeUse: newId }));
    }
  }, [location.state, propFormData]);

  // Effect to load existing application data when resuming a draft
  useEffect(() => {
    // If formData is passed as a prop, don't load draft data from the server.
    if (propFormData) {
      return;
    }

    const loadDraftData = async () => {
      if (applicationId && !isNewlyGeneratedIdRef.current && !isViewMode) {
        console.log(`[ApplicationFormPage11 LoadDraft] Attempting to load draft data for ID: ${applicationId}`);
        try {
          const appData = await fetchApplicationDetails(applicationId);
          if (appData && appData.formData) {
            const loadedFormData = {
              ...appData.formData,
              // Handle any file fields if needed
            };
            setFormData(loadedFormData);
            console.log(`[ApplicationFormPage11 LoadDraft] Successfully loaded draft data for ID: ${applicationId}`, appData);
          } else {
            console.warn(`[ApplicationFormPage11 LoadDraft] No application data or formData found for ID: ${applicationId}`, appData);
          }
        } catch (error) {
          console.error(`[ApplicationFormPage11 LoadDraft] Error loading draft data for ${applicationId}:`, error);
        }
      }
    };

    loadDraftData();
  }, [applicationId, isViewMode, propFormData]);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleOpenFilePreview = (file: File | string | null, defaultFileName: string = 'file') => {
    console.log('[handleOpenFilePreview] Called with file:', file, 'defaultFileName:', defaultFileName);
    if (!file) {
      console.log('[handleOpenFilePreview] File is null, returning.');
      return;
    }

    console.log('[handleOpenFilePreview] Current API_BASE_URL:', API_BASE_URL);

    let url: string | null = null;
    let name: string | null = null;
    let type: string | null = null;

    if (typeof file === 'string') {
      console.log('[handleOpenFilePreview] File is a string:', file);

      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const urlObject = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${urlObject.protocol}//${urlObject.host}`;
        } catch (e) {
          console.warn('[handleOpenFilePreview] Could not parse REACT_APP_API_URL, using window.origin instead:', e);
          serverBaseForFiles = window.location.origin;
        }
      }

      let normalizedPath = file;
      if (file.includes('://')) {
        try { normalizedPath = new URL(file).pathname; } catch { normalizedPath = file; }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = normalizedPath.includes('/') ? `/${normalizedPath}` : `/uploads/${normalizedPath}`;
      }

      url = `${serverBaseForFiles}${normalizedPath}`;
      name = normalizedPath.split('/').pop() || defaultFileName;

      const lower = name.toLowerCase();
      if (lower.endsWith('.pdf')) {
        type = 'application/pdf';
      } else if (lower.match(/\.(jpeg|jpg|gif|png)$/i)) {
        const ext = lower.split('.').pop();
        type = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      } else {
        type = 'application/octet-stream';
      }
      console.log(`[handleOpenFilePreview] Constructed URL: ${url}, Name: ${name}, Type: ${type}`);
    } else {
      console.log('[handleOpenFilePreview] File is a File object:', file);
      url = URL.createObjectURL(file);
      name = file.name;
      type = file.type;
      console.log('[handleOpenFilePreview] File object - Constructed URL:', url, 'Name:', name, 'Type:', type);
    }

    setModalFileUrl(url);
    setModalFileName(name);
    setModalFileType(type);
    setIsModalOpen(true);
    console.log('[handleOpenFilePreview] Modal state set. isOpen:', true, 'Url:', url);
  };

  const handleDownloadPdf = async () => {
    setIsPdfGenerating(true);
    setCompressionProgress(0);
    const formElement = formRef.current;
    if (!formElement) {
      alert('Form content is not available for PDF export.');
      setIsPdfGenerating(false);
      return;
    }

    const formClone = formElement.cloneNode(true) as HTMLElement;

    try {
      formClone.style.position = 'absolute';
      formClone.style.left = '-9999px';
      formClone.style.top = '0px';
      formClone.style.width = `${formElement.offsetWidth}px`;
      document.body.appendChild(formClone);

      const inputs = formClone.querySelectorAll('input[type="text"], input[type="number"], textarea');
      inputs.forEach(input => {
        const typedInput = input as HTMLInputElement | HTMLTextAreaElement;
        const value = typedInput.value;
        if (value) {
          const span = document.createElement('span');
          span.textContent = value;
          span.style.borderBottom = '1px solid #333';
          span.style.padding = '2px 5px';
          span.style.margin = '0 5px';
          span.style.fontSize = 'inherit';
          span.style.fontFamily = 'inherit';
          span.style.display = 'inline-block';
          span.style.whiteSpace = 'pre-wrap';
          span.style.wordBreak = 'break-word';
          span.style.color = window.getComputedStyle(typedInput).color;
          typedInput.parentNode?.replaceChild(span, typedInput);
        }
      });

      const checkboxes = formClone.querySelectorAll('input[type="checkbox"], input[type="radio"]');
      checkboxes.forEach(checkbox => {
        const typedCheckbox = checkbox as HTMLInputElement;
        const span = document.createElement('span');
        span.textContent = typedCheckbox.checked ? '☑' : '☐';
        span.style.fontSize = 'inherit';
        span.style.fontFamily = 'inherit';
        span.style.display = 'inline-block';
        span.style.margin = '0 5px';
        typedCheckbox.parentNode?.replaceChild(span, typedCheckbox);
      });

      const buttons = formClone.querySelectorAll('button');
      buttons.forEach(btn => (btn.style.visibility = 'hidden'));

      const canvas = await html2canvas(formClone, {
        scale: 2,
        useCORS: true,
        width: formClone.scrollWidth,
        height: formClone.scrollHeight,
        windowWidth: formClone.scrollWidth,
        windowHeight: formClone.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.5); // Use aggressive JPEG compression
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'l' : 'p',
        unit: 'px',
        format: [canvas.width, canvas.height],
      });

      pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height);
      
      // Simulate compression process for visual feedback
      for (let i = 0; i <= 100; i += 10) {
        setCompressionProgress(i);
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      pdf.save(`application_form_page11_${applicationId || 'draft'}.pdf`);
    } catch (error) {
      console.error('PDF Generation Error:', error);
      alert(`Error generating PDF for Page 11. Please check the console for details. Error: ${error instanceof Error ? error.message : String(error)}`);
      // Ensure buttons are visible even if an error occurs
      const formClone = document.querySelector('body > *:last-child') as HTMLElement;
      if (formClone) {
        const buttons = formClone.querySelectorAll('button');
        buttons.forEach(btn => btn.style.visibility = 'visible');
      }
    } finally {
      const formClone = document.querySelector('body > *:last-child') as HTMLElement;
      if (formClone && formClone.parentNode === document.body) {
        document.body.removeChild(formClone);
      }
      setIsPdfGenerating(false);
      setCompressionProgress(0);
    }
  };

  // Function to save data to database
  const saveToDatabaseCallback = useCallback(async (data: typeof formData, isDraft: boolean, anId?: string) => {
    const idToUse = anId || applicationId;

    if (!idToUse) {
      console.error('Application ID is missing, cannot save.');
      setSaveMessage('Error: Application ID missing.');
      setIsSaving(false);
      return;
    }

    if (!isOnline) {
      console.warn('Offline: Save operation skipped for application ID:', idToUse);
      setSaveMessage('Offline: Changes will be saved when back online.');
      return;
    }

    console.log(`[SaveToDB] Attempting to save. ID: ${idToUse}, isDraft: ${isDraft}`);
    setIsSaving(true);
    const currentSaveMessage = isDraft ? 'Saving Draft...' : 'Submitting Application...';
    setSaveMessage(currentSaveMessage);
    setShowSaveIndicator(true);
    
    try {
      const dataToSave = {
        ...data,
        // Handle any file fields if needed
      };
      
      const response = await saveFormData(idToUse, dataToSave, isDraft);
      
      if (response.success) {
        const successMsg = isDraft ? 'Draft saved successfully' : 'Application submitted successfully';
        console.log(`[SaveToDB] Success for ID: ${idToUse}. Message: ${successMsg}`);
        setSaveMessage(successMsg);
        if (isDraft && isNewlyGeneratedIdRef.current) {
          isNewlyGeneratedIdRef.current = false;
          console.log('[SaveToDB] Reset isNewlyGeneratedIdRef for ID:', idToUse);
        }
        setTimeout(() => setShowSaveIndicator(false), 2000);
      } else {
        setSaveMessage('Error saving');
        setTimeout(() => setShowSaveIndicator(false), 3000);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
      setSaveMessage('Error saving');
      setTimeout(() => setShowSaveIndicator(false), 3000);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, isOnline]);

  // Effect for initial auto-save of a newly generated ID
  useEffect(() => {
    if (applicationId && isNewlyGeneratedIdRef.current && isOnline && Object.keys(formData).some(key => formData[key as keyof FormData] !== '' && key !== 'errors' && key !== 'applicationNoOfficeUse')) {
      console.log(`[ApplicationFormPage11 Effect AutoSave] Application ID ${applicationId} is newly generated. Attempting to save as draft.`);
      saveToDatabaseCallback(formData, true, applicationId);
    }
  }, [applicationId, formData, saveToDatabaseCallback, isOnline]);

  const handleFillDummyData = () => {
    console.log('[ApplicationFormPage11 FillDummyData] Filling form with dummy data.');
    setFormData(prevData => ({
      ...prevData,
      applicationNoOfficeUse: prevData.applicationNoOfficeUse || applicationId || '',
      customerName: 'Green Energy Co-operative Society Ltd.',
      yearsAsCustomer: '5',
      facilityA: 'Current Account - A/C No. 1234567890',
      facilityB: 'Term Loan - Rs. 50,00,000/- for business expansion',
      facilityC: 'Letter of Credit facility - Limit Rs. 25,00,000/-',
      bankName: 'State Bank of India, Pune Main Branch',
      loanAmountNumber: '1,00,00,000',
      loanAmountWords: 'One Crore Only',
      signatoryName: 'Mr. Anil Kumar Verma',
      signatoryDesignation: 'Branch Manager',
      certificateDate: '15/01/2025',
      errors: {},
    }));
    setSaveMessage('Dummy data filled. Save to persist.');
    setShowSaveIndicator(true);
    setTimeout(() => {
      setShowSaveIndicator(false);
    }, 3000);
  };

  const debouncedSave = useCallback((newData: FormData) => {
    console.log('[DebouncedSave] Invoked. Clearing previous timeout if any.');
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      console.log('[DebouncedSave] Timeout executed. Checking conditions to save.');
      if (applicationId) {
        console.log(`[DebouncedSave] Conditions met. ApplicationId: ${applicationId}. Proceeding to save.`);
        console.log(`[DebouncedSave] Triggering auto-save (as draft) for ID: ${applicationId}`);
        saveToDatabaseCallback({ ...newData, applicationNoOfficeUse: applicationId }, true, applicationId);
      } else {
        console.log(`[DebouncedSave] Conditions NOT met. Skipping save. ApplicationId: ${applicationId}`);
      }
    }, 1000);
  }, [saveToDatabaseCallback, applicationId]);

  // Removed interval-based auto-save - using navigation-based save modal instead

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);
    // Removed auto-save on every keystroke - use SAVE DRAFT button or navigation modal instead
  };

  const handleZoomIn = () => {
    setZoomScale(prev => Math.min(prev + 0.1, 1.5));
  };
  
  const handleZoomOut = () => {
    setZoomScale(prev => Math.max(prev - 0.1, 0.6));
  };

  const handleNextButtonClick = async () => {
    if (applicationId && isOnline) {
      console.log('[Navigation Page11] Saving draft before navigating');
      await saveToDatabaseCallback({ ...formData, applicationNoOfficeUse: applicationId }, true, applicationId);
    }
    if (applicationId && applicationId !== formData.applicationNoOfficeUse) {
      setFormData(prev => ({
        ...prev,
        applicationNoOfficeUse: applicationId
      }));
    }
    navigate('/form/page12');
  };

  // Calculate form progress whenever form data changes
  useEffect(() => {
    const totalFields = 11; // All text fields
    let filledFields = 0;
    
    // Check text fields
    if (formData.customerName && formData.customerName.trim() !== '') filledFields++;
    if (formData.yearsAsCustomer && formData.yearsAsCustomer.trim() !== '') filledFields++;
    if (formData.facilityA && formData.facilityA.trim() !== '') filledFields++;
    if (formData.facilityB && formData.facilityB.trim() !== '') filledFields++;
    if (formData.facilityC && formData.facilityC.trim() !== '') filledFields++;
    if (formData.bankName && formData.bankName.trim() !== '') filledFields++;
    if (formData.loanAmountNumber && formData.loanAmountNumber.trim() !== '') filledFields++;
    if (formData.loanAmountWords && formData.loanAmountWords.trim() !== '') filledFields++;
    if (formData.signatoryName && formData.signatoryName.trim() !== '') filledFields++;
    if (formData.signatoryDesignation && formData.signatoryDesignation.trim() !== '') filledFields++;
    if (formData.certificateDate && formData.certificateDate.trim() !== '') filledFields++;

    const progressPercentage = totalFields > 0 ? (filledFields / totalFields) * 100 : 0;
    setFormProgress(progressPercentage);
  }, [formData]);

  // Effect for online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const ModalContent = styled.div`
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    text-align: center;
  `;

  const ProgressBar = styled.div`
    width: 100%;
    height: 20px;
    background-color: #e0e0e0;
    border-radius: 10px;
    margin-top: 20px;
    overflow: hidden;
  `;

  const ProgressFill = styled.div<{ progress: number }>`
    height: 100%;
    width: ${props => props.progress}%;
    background-color: #6cb288;
    transition: width 0.3s ease-in-out;
  `;

  const ProgressText = styled.p`
    margin-top: 10px;
    font-size: 14px;
    color: #333;
  `;

  return (
    <>
      {isPdfGenerating && (
        <ModalOverlay>
          <ModalContent>
            <h3>Generating PDF</h3>
            <p>Please wait while we compress and generate your PDF...</p>
            <ProgressBar>
              <ProgressFill progress={compressionProgress} />
            </ProgressBar>
            <ProgressText>{compressionProgress}% Complete</ProgressText>
          </ModalContent>
        </ModalOverlay>
      )}
      {/* Progress bar */}
      <ProgressBarContainer>
        <ProgressBarFill progress={formProgress} />
      </ProgressBarContainer>
      <ProgressLabel visible={true}>Page 11/14 • {Math.round(formProgress)}% complete</ProgressLabel>
      
      {/* Zoom controls */}
      <ZoomControlsContainer>
        <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
        <ZoomButton onClick={handleZoomOut}>−</ZoomButton>
      </ZoomControlsContainer>
      
      {/* Save indicator */}
      <SaveIndicator visible={showSaveIndicator}>
        {saveMessage}
      </SaveIndicator>

      {!isOnline && (
        <OfflineIndicator>
          You are currently offline. Changes will not be saved until you reconnect.
        </OfflineIndicator>
      )}
      
      <FloatingBackButton onClick={() => navigate('/form/page10')} aria-label="Back to previous page">
        ←
      </FloatingBackButton>
      
      {showDummyDataButton && (
        <div style={{ position: 'fixed', top: '80px', right: '30px', zIndex: 1001, display: 'block' }}>
          <button 
            onClick={handleFillDummyData} 
            style={{ 
              backgroundColor: '#ffc107', 
              color: '#212529', 
              borderRadius: '4px', 
              padding: '8px 15px',
              border: '1px solid #e0a800',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 2px 5px rgba(0,0,0,0.2)'
            }}
          >
            Fill Dummy Data
          </button>
        </div>
      )}
      
      <FormPageNavigator currentPage={11} totalPages={14} targetFormRef={formRef} zoomScale={zoomScale} />
      
      <div style={{ position: 'fixed', right: '-25px', top: '605px', zIndex: 999 }}>
        <NextPageButton 
          onClick={handleNextButtonClick}
          style={{ 
            height: '35px', 
            width: '140px', 
            borderRadius: '4px', 
            whiteSpace: 'nowrap',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: '0 10px'
          }}
        >
          Next Page 12/14 →
        </NextPageButton>
      </div>
      
      <PageWrapper scale={zoomScale}>
        <A4PageContainer ref={formRef}>
          <FormContentContainer>
            <CertificateTitle>APPENDIX - C</CertificateTitle>
            <Subtitle>
              (CREDIT WORTHINESS CERTIFICATE FROM THE BANKERS, FROM SCHEDULED BANK/ FINANCIAL<br />
              INSTITUTIONS TO BE OBTAINED ON THEIR LETTER HEAD IN THE FOLLOWING FORMAT AND<br />
              ENCLOSED WITH THE APPLICATION)
            </Subtitle>
            
            <ToWhomSection>To whomsoever it may concern</ToWhomSection>
            
            <CertificateText>
              Shri /Ms./M/s. 
              <InputField 
                type="text" 
                name="customerName" 
                value={formData.customerName}
                onChange={handleChange}
                placeholder="Customer Name"
                style={{ minWidth: '250px' }}
              />
              is a customer of this bank / institution for last 
              <InputField 
                type="text" 
                name="yearsAsCustomer" 
                value={formData.yearsAsCustomer}
                onChange={handleChange}
                placeholder="Years"
                style={{ minWidth: '60px' }}
              />
              years and he / she / they, is/are enjoying the following facilities of our bank / institution:
            </CertificateText>
            
            <FacilityList>
              <FacilityItem>
                <FacilityLabel>a.</FacilityLabel>
                <FacilityInput 
                  type="text" 
                  name="facilityA" 
                  value={formData.facilityA}
                  onChange={handleChange}
                  placeholder="Banking facility details"
                />
              </FacilityItem>
              <FacilityItem>
                <FacilityLabel>b.</FacilityLabel>
                <FacilityInput 
                  type="text" 
                  name="facilityB" 
                  value={formData.facilityB}
                  onChange={handleChange}
                  placeholder="Banking facility details"
                />
              </FacilityItem>
              <FacilityItem>
                <FacilityLabel>c.</FacilityLabel>
                <FacilityInput 
                  type="text" 
                  name="facilityC" 
                  value={formData.facilityC}
                  onChange={handleChange}
                  placeholder="Banking facility details"
                />
              </FacilityItem>
            </FacilityList>
            
            <CertificateText>
              During the above period, the dealing of Shri /Ms./ M/s. 
              <InputField 
                type="text" 
                name="customerName2" 
                value={formData.customerName}
                onChange={(e) => handleChange({ ...e, target: { ...e.target, name: 'customerName' } })}
                placeholder="Customer Name"
                style={{ minWidth: '250px' }}
              />
              with the bank 
              <InputField 
                type="text" 
                name="bankName" 
                value={formData.bankName}
                onChange={handleChange}
                placeholder="Bank Name"
                style={{ minWidth: '250px' }}
              />
              and he / her / their financial risk seems likely to be in case, Dealership of CNG Station is advised to him / her / them, we will be willing to extend a loan of Rs. 
              <InputField 
                type="text" 
                name="loanAmountNumber" 
                value={formData.loanAmountNumber}
                onChange={handleChange}
                placeholder="Amount"
                style={{ minWidth: '120px' }}
              />
              (Rupees 
              <InputField 
                type="text" 
                name="loanAmountWords" 
                value={formData.loanAmountWords}
                onChange={handleChange}
                placeholder="Amount in words"
                style={{ minWidth: '250px' }}
              />) (in words).
            </CertificateText>
            
            <ClosingSection>
              <SignatureBox>
                <SignatureLine />
                <SignatureLabel>Signature</SignatureLabel>
              </SignatureBox>
              
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div style={{ marginBottom: '10px', width: '250px' }}>
                  <SignatureLabel>Name and Designation:</SignatureLabel>
                  <InputField 
                    type="text" 
                    name="signatoryName" 
                    value={formData.signatoryName}
                    onChange={handleChange}
                    placeholder="Name"
                    style={{ width: '100%', marginBottom: '5px' }}
                  />
                  <InputField 
                    type="text" 
                    name="signatoryDesignation" 
                    value={formData.signatoryDesignation}
                    onChange={handleChange}
                    placeholder="Designation"
                    style={{ width: '100%' }}
                  />
                </div>
                
                <SealBox>Office Seal</SealBox>
                
                <DateField>
                  <SignatureLabel style={{ marginRight: '10px' }}>Date:</SignatureLabel>
                  <InputField 
                    type="text" 
                    name="certificateDate" 
                    value={formData.certificateDate}
                    onChange={handleChange}
                    placeholder="DD/MM/YYYY"
                    style={{ minWidth: '120px' }}
                  />
                </DateField>
              </div>
            </ClosingSection>
          </FormContentContainer>
          <PageNumber>11</PageNumber>
        </A4PageContainer>
        
        <FilePreviewModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          fileUrl={modalFileUrl}
          fileName={modalFileName}
          fileType={modalFileType}
        />
      </PageWrapper>
      
      {/* Empty ActionButtonsContainer to maintain structure */}
      <ActionButtonsContainer style={{ position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '800px', zIndex: 1000 }}>
        {/* Next Page button moved near FormPageNavigator */}
      </ActionButtonsContainer>
      
      {/* Session Timer */}
      {!isViewMode && (
        <SessionTimer 
          sessionDuration={30}
          warningTime={5}
          onSessionExpired={() => navigate('/')}
          onSaveState={async () => {
            if (applicationId) {
              await saveToDatabaseCallback(formData, true, applicationId);
            }
          }}
        />
      )}

      {/* Home Button - Above Download PDF */}
      <button
        onClick={() => navigate('/user-dashboard')}
        title="Go to Dashboard"
        style={{
          position: 'fixed',
          left: '95px',
          bottom: isViewMode ? '100px' : '175px',
          zIndex: 1000,
          width: '145px',
          height: '45px',
          borderRadius: '50px',
          border: 'none',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '13px',
          fontWeight: '600',
          boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
          transition: 'all 0.2s ease'
        }}
      >
        🏠 Dashboard
      </button>

      {!isViewMode && (
        <>
          <PayNowButton 
            onClick={handleDownloadPdf} 
            disabled={!isOnline || isViewMode}
            style={{ position: 'fixed', left: '95px', bottom: '105px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
          >
            DOWNLOAD PDF
          </PayNowButton>
          
          <SaveDraftButton 
            onClick={() => saveToDatabaseCallback(formData, true, applicationId)} 
            disabled={isSaving || !applicationId || !isOnline}
            style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', textAlign: 'center', fontSize: '12px', fontWeight: 'bold', letterSpacing: '0.2px' }}
          >
            {isSaving && saveMessage.startsWith('Saving Draft') ? 'Saving...' : 'SAVE DRAFT'}
          </SaveDraftButton>
        </>
      )}
      
      {isViewMode && (
        <PayNowButton 
          onClick={handleDownloadPdf} 
          disabled={!isOnline}
          style={{ position: 'fixed', left: '95px', bottom: '31.5px', zIndex: 1000, margin: '0', height: '57px', borderRadius: '4px', width: '145px', fontSize: '12px', whiteSpace: 'nowrap', textAlign: 'center', fontWeight: 'bold', letterSpacing: '0.2px' }}
        > 
          DOWNLOAD PDF
        </PayNowButton>
      )}
    </>
  );
};

export default ApplicationFormPage11;